import { writable } from 'svelte/store';

export enum DragType {
    SWAP = "swap",
    MOVE = "move",
    COPY = "copy",
    IMPORT = 'import'
}

export interface DragInformation {
    type: DragType;
    data?: any;
}

function createSingleton() {
  const { subscribe, set, update } = writable<DragInformation>(null);

  return {
    subscribe,
    set,
    update,
    reset: () => set(null)
  };
}

export const dragInformation = createSingleton();

// Chrome on android seems to only support "text/plain"
const format = "text/plain";
// const format = "application/json";

const debugMessages = false;

export function draggable(node, state) {
    let disableTimeout;

    if (state.disabled) return { update(data) { state = data; }, destroy() {} };

    node.draggable = true;
    node.style.cursor = 'grab';

    function handle_dragstart(e) {
        if (debugMessages) console.log('dragstart', e);
        if (!e.dataTransfer) return;
        state.on_dragstart(state['data'].operation);

        if (state['image']) {
            state['data'].image_div_id = state['image'].id;
            disableTimeout = setTimeout(() => state['image'].style.pointerEvents = 'none', 100);
            e.dataTransfer.setDragImage(state['image'], state['image'].offsetWidth/2, state['image'].offsetHeight/2);
        }
        e.dataTransfer.effectAllowed = state['data'].operation;
        e.dataTransfer.setData(format, JSON.stringify(state['data']));
    }

    function handle_dragend(e) {
        if (debugMessages) console.log('dragend', e);
        clearTimeout(disableTimeout);
        state['image']?.style?.removeProperty('pointer-events');
        state.on_dragend();
        dragInformation.reset();
    }

    function handle_drag(e) {
        if (state.onDrag) {
            state.onDrag(e.clientX, e.clientY);
        }
    }

    node.addEventListener('dragstart', handle_dragstart);
    node.addEventListener('dragend', handle_dragend);
    node.addEventListener('drag', handle_drag);

    return {
        update(data) {
            state = data;
        },

        destroy() {
            node.removeEventListener('dragstart', handle_dragstart);
            node.removeEventListener('dragend', handle_dragend);
        }
    };
}

export function dropzone(node, options) {
    let state = {
        dragover_class: 'droppable',
        ...options
    };

    function handle_dragenter(e) {
        if (debugMessages) console.log('dragenter', e);
        if (!(e.target instanceof HTMLElement)) return;
        e.target.classList.add(state.dragover_class);
        if (state.on_dragenter) state.on_dragenter();
    }

    function handle_dragleave(e) {
        if (debugMessages) console.log('dragleave', e);
        if (!(e.target instanceof HTMLElement)) return;
        e.target.classList.remove(state.dragover_class);
        if (state.on_dragleave) state.on_dragleave();
    }

    function handle_dragover(e) {
        e.preventDefault();
        e.stopPropagation();
        if (!e.dataTransfer) return;
        // NOTE: dragover doesn't allow getting data from dataTransfer.
        // const text = e.dataTransfer.getData(format)
        // if (text) {
        //     const data = JSON.parse(text)
        //     e.dataTransfer.dropEffect = data.operation;
        // }
    }

    function handle_drop(e) {
        if (debugMessages) console.log('drop', e);
        e.preventDefault();
        e.stopPropagation();
        if (!e.dataTransfer) return;

        let data: any
        let operation: DragType
        if (e.dataTransfer.files.length > 0) {
            // Only use 1 file.
            data = e.dataTransfer.files[0];
            operation = DragType.IMPORT;
        } else {
            const text = e.dataTransfer.getData(format);
            if (!text) return;
            data = JSON.parse(text);
            operation = data.operation;
        }

        if (!(e.target instanceof HTMLElement)) return;

        e.target.classList.remove(state.dragover_class);
        const image_div_id = data.image_div_id;
        if (image_div_id) {
            const image_div = document.getElementById(image_div_id);
            image_div?.style?.removeProperty('pointer-events');
        }
        state.on_dropzone(data, operation);
        dragInformation.reset();
    }

    node.addEventListener('dragenter', handle_dragenter);
    node.addEventListener('dragleave', handle_dragleave);
    node.addEventListener('dragover', handle_dragover);
    node.addEventListener('drop', handle_drop);

    return {
        update(options) {
            state = {
                dropEffect: 'move',
                dragover_class: 'droppable',
                ...options
            };
        },

        destroy() {
            node.removeEventListener('dragenter', handle_dragenter);
            node.removeEventListener('dragleave', handle_dragleave);
            node.removeEventListener('dragover', handle_dragover);
            node.removeEventListener('drop', handle_drop);
        }
    };
}
